if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.formId = this.form.getAttribute('id');
        this.variantIdInput = this.form.querySelector('input[name="id"]');

        if (this.variantIdInput) {
          this.variantIdInput.disabled = false;
        }

        this.form.addEventListener(
          'submit',
          this.onSubmitHandler.bind(this)
        );

        /*
         * Starts the page without any product option selected.
         * This applies to regular buttons, color swatches and dropdowns.
         */
        this.resetVariantSelections();

        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        this.submitButton = this.querySelector('[type="submit"]');
        this.upsellVariantId = this.dataset.upsellVariantId;

        if (document.querySelector('cart-drawer')) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      resetVariantSelections() {
        if (!this.formId) return;

        document
          .querySelectorAll(
            `variant-radios input[type="radio"][form="${this.formId}"]`
          )
          .forEach((radio) => {
            radio.checked = false;
          });

        document
          .querySelectorAll(
            `variant-selects select[form="${this.formId}"]`
          )
          .forEach((select) => {
            select.value = '';
          });

        document
          .querySelectorAll(
            `variant-radios input[type="radio"][form="${this.formId}"]`
          )
          .forEach((radio) => {
            const fieldset = radio.closest('fieldset');
            const selectedText = fieldset?.querySelector(
              '.form__label span[id^="selected-"]'
            );

            if (selectedText) {
              selectedText.textContent = 'Select';
            }
          });
      }

      getVariantSelection() {
        if (!this.formId) {
          return {
            hasVariantControls: false,
            missingOptions: [],
            selectedVariant: null,
            firstMissingElement: null,
          };
        }

        const radios = Array.from(
          document.querySelectorAll(
            `variant-radios input[type="radio"][form="${this.formId}"]`
          )
        );

        const selects = Array.from(
          document.querySelectorAll(
            `variant-selects select[form="${this.formId}"]`
          )
        );

        if (radios.length > 0) {
          const optionNames = [
            ...new Set(
              radios.map((radio) => radio.getAttribute('name'))
            ),
          ];

          const missingOptions = [];
          let firstMissingElement = null;

          const selectedValues = optionNames.map((optionName) => {
            const groupRadios = radios.filter(
              (radio) => radio.getAttribute('name') === optionName
            );

            const selectedRadio = groupRadios.find(
              (radio) => radio.checked
            );

            if (!selectedRadio) {
              missingOptions.push(optionName);

              if (!firstMissingElement) {
                firstMissingElement = groupRadios[0] || null;
              }

              return '';
            }

            return selectedRadio.value;
          });

          return {
            hasVariantControls: true,
            missingOptions,
            selectedVariant: this.findSelectedVariant(
              radios[0]?.closest('variant-radios'),
              selectedValues
            ),
            firstMissingElement,
          };
        }

        if (selects.length > 0) {
          const missingOptions = [];
          let firstMissingElement = null;

          const selectedValues = selects.map((select) => {
            if (!select.value) {
              const optionName =
                select
                  .closest('[data-option-name]')
                  ?.getAttribute('data-option-name') ||
                select.getAttribute('name') ||
                'option';

              missingOptions.push(optionName);

              if (!firstMissingElement) {
                firstMissingElement = select;
              }
            }

            return select.value;
          });

          return {
            hasVariantControls: true,
            missingOptions,
            selectedVariant: this.findSelectedVariant(
              selects[0]?.closest('variant-selects'),
              selectedValues
            ),
            firstMissingElement,
          };
        }

        return {
          hasVariantControls: false,
          missingOptions: [],
          selectedVariant: null,
          firstMissingElement: null,
        };
      }

      findSelectedVariant(variantPicker, selectedValues) {
        if (
          !variantPicker ||
          selectedValues.length === 0 ||
          selectedValues.some((value) => !value)
        ) {
          return null;
        }

        const variantDataElement = variantPicker.querySelector(
          'script[type="application/json"]'
        );

        if (!variantDataElement) return null;

        try {
          const variants = JSON.parse(
            variantDataElement.textContent
          );

          return (
            variants.find((variant) => {
              return variant.options.every((option, index) => {
                return selectedValues[index] === option;
              });
            }) || null
          );
        } catch (error) {
          console.error(
            'Unable to read product variant data.',
            error
          );

          return null;
        }
      }

      getMissingOptionsMessage(missingOptions) {
        if (missingOptions.length !== 1) {
          return 'Please select all product options before continuing.';
        }

        const optionName = missingOptions[0]
          .toLowerCase()
          .trim();

        if (
          optionName.includes('size') ||
          optionName.includes('shoe size')
        ) {
          return 'Please select a size before continuing.';
        }

        if (optionName.includes('height')) {
          return 'Please select a height before continuing.';
        }

        if (
          optionName.includes('color') ||
          optionName.includes('colour')
        ) {
          return 'Please select a color before continuing.';
        }

        return `Please select ${optionName} before continuing.`;
      }

      showVariantError(message, element = null) {
        this.handleErrorMessage(message);

        if (element) {
          const optionContainer =
            element.closest('.product-form__input') ||
            element;

          optionContainer.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });

          if (
            typeof element.focus === 'function' &&
            element.tagName === 'SELECT'
          ) {
            element.focus({
              preventScroll: true,
            });
          }
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        if (
          this.submitButton.getAttribute('aria-disabled') ===
          'true'
        ) {
          return;
        }

        /*
         * Requires the customer to select every product option
         * before adding the product to cart or going to checkout.
         */
        const variantSelection = this.getVariantSelection();

        if (
          variantSelection.hasVariantControls &&
          variantSelection.missingOptions.length > 0
        ) {
          this.showVariantError(
            this.getMissingOptionsMessage(
              variantSelection.missingOptions
            ),
            variantSelection.firstMissingElement
          );

          return;
        }

        if (
          variantSelection.hasVariantControls &&
          !variantSelection.selectedVariant
        ) {
          this.showVariantError(
            'Please select a valid combination of product options before continuing.'
          );

          return;
        }

        if (
          variantSelection.selectedVariant &&
          !variantSelection.selectedVariant.available
        ) {
          this.showVariantError(
            'The selected option combination is currently unavailable.'
          );

          return;
        }

        /*
         * Writes the validated variant ID into this form.
         * This prevents the hidden first variant from being submitted.
         */
        if (
          variantSelection.hasVariantControls &&
          variantSelection.selectedVariant
        ) {
          const variantInput =
            this.form.querySelector('input[name="id"]');

          if (variantInput) {
            variantInput.value =
              variantSelection.selectedVariant.id;

            variantInput.disabled = false;
          }
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute(
          'aria-disabled',
          true
        );

        this.submitButton.classList.add('loading');

        const loadingSpinner = this.querySelector(
          '.loading__spinner'
        );

        if (loadingSpinner) {
          loadingSpinner.classList.remove('hidden');
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] =
          'XMLHttpRequest';

        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        if (this.cart) {
          formData.append(
            'sections',
            this.cart
              .getSectionsToRender()
              .map((section) => section.id)
          );

          formData.append(
            'sections_url',
            window.location.pathname
          );

          this.cart.setActiveElement(
            document.activeElement
          );
        }

        config.body = formData;

        this.skipCart = false;

        const productIdInput =
          this.form.querySelector('[name="id"]');

        if (
          productIdInput?.dataset.skipCart === 'true'
        ) {
          this.skipCart = true;
        }

        fetch(`${routes.cart_url}.js`)
          .then((response) => response.json())
          .then((cartState) => {
            if (
              cartState.item_count === 0 &&
              this.upsellVariantId
            ) {
              return fetch(
                `${routes.cart_add_url}`,
                this.getUpsellProductConfig()
              );
            }
          })
          .then(() => {
            return fetch(
              `${routes.cart_add_url}`,
              config
            );
          })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId:
                  formData.get('id'),
                errors:
                  response.errors ||
                  response.description,
                message: response.message,
              });

              this.handleErrorMessage(
                response.description
              );

              const soldOutMessage =
                this.submitButton.querySelector(
                  '.sold-out-message'
                );

              if (!soldOutMessage) return;

              this.submitButton.setAttribute(
                'aria-disabled',
                true
              );

              this.submitButton
                .querySelector('span')
                ?.classList.add('hidden');

              soldOutMessage.classList.remove('hidden');
              this.error = true;

              return;
            }

            if (this.skipCart) {
              window.location = '/checkout';
              return;
            }

            if (!this.cart) {
              window.location =
                window.routes.cart_url;

              return;
            }

            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId:
                  formData.get('id'),
                cartData: response,
              });
            }

            this.error = false;

            const quickAddModal =
              this.closest('quick-add-modal');

            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                {
                  once: true,
                }
              );

              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.submitButton.classList.remove(
              'loading'
            );

            if (
              this.cart &&
              this.cart.classList.contains('is-empty')
            ) {
              this.cart.classList.remove('is-empty');
            }

            if (!this.error) {
              this.submitButton.removeAttribute(
                'aria-disabled'
              );
            }

            if (loadingSpinner) {
              loadingSpinner.classList.add('hidden');
            }
          });
      }

      getUpsellProductConfig() {
        const upsellFormData = new FormData();

        upsellFormData.append(
          'id',
          this.upsellVariantId
        );

        upsellFormData.append('quantity', 1);

        const config = fetchConfig('javascript');

        config.headers['X-Requested-With'] =
          'XMLHttpRequest';

        delete config.headers['Content-Type'];

        config.body = upsellFormData;

        return config;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(
            '.product-form__error-message-wrapper'
          );

        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            '.product-form__error-message'
          );

        this.errorMessageWrapper.toggleAttribute(
          'hidden',
          !errorMessage
        );

        if (errorMessage) {
          this.errorMessage.textContent =
            errorMessage;
        }
      }
    }
  );
}